import { ReactNode, useEffect, useMemo } from 'react'
import {
  initialQueryState,
  OptionsHelper,
  QueryFilter,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useListView } from '../../core/ListViewProvider'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: barra "Gerar fatura" da seleção). */
  actions?: ReactNode
}

export const FinanceiroAtendimentosListSearchComponent = ({ actions }: Props) => {
  const { t } = useT()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { clearSelected } = useListView()
  const { filters, setFilter, clearFilters } = useListFilters()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const queryParams = useMemo(() => {
    const search = [
      debouncedFilters.empresa ? `paciente.empresa.id:${debouncedFilters.empresa.value}` : '',
      debouncedFilters.paciente ? `pessoa.nome~*${debouncedFilters.paciente.trim()}*` : '',
      debouncedFilters.protocolo ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
      debouncedFilters.especialidade ? `especialidade:${debouncedFilters.especialidade.value}` : '',
    ]
      .filter(Boolean)
      .join(',')

    // Mês e ano vão como filtro exato (`filters`), não no `search`.
    const queryFilters: QueryFilter[] = []
    if (debouncedFilters.ano)
      queryFilters.push({ field: 'ano', operator: '=', value: Number(debouncedFilters.ano.value) })
    if (debouncedFilters.mes)
      queryFilters.push({ field: 'mes', operator: '=', value: Number(debouncedFilters.mes.value) })

    return { search, filters: queryFilters }
  }, [debouncedFilters])

  // Filtro novo invalida a seleção (a fatura é gerada só com o que está na tela filtrada).
  useEffect(() => {
    clearSelected()
    updateState({
      ...initialQueryState,
      search: queryParams.search,
      filters: queryParams.filters,
    })
  }, [queryParams, clearSelected, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.mes && {
      key: 'mes',
      label: t('mes.label'),
      value: filters.mes.label,
      onRemove: () => setFilter('mes', undefined),
    },
    filters.ano && {
      key: 'ano',
      label: t('ano.label'),
      value: filters.ano.label,
      onRemove: () => setFilter('ano', undefined),
    },
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('paciente.label'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('protocolo.label'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.especialidade && {
      key: 'especialidade',
      label: t('especialidade.label'),
      value: filters.especialidade.label,
      onRemove: () => setFilter('especialidade', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [
    filters.paciente.trim(),
    filters.protocolo.trim(),
    filters.especialidade,
  ].filter(Boolean).length

  return (
    <TableFilters
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label={t('paciente.label')}>
            <Input
              entity='paciente'
              value={filters.paciente}
              onChange={(e) => setFilter('paciente', e.target.value)}
            />
          </TableFilter>
          <TableFilter label={t('protocolo.label')}>
            <Input
              entity='protocolo'
              value={filters.protocolo}
              onChange={(e) => setFilter('protocolo', e.target.value)}
            />
          </TableFilter>
          <TableFilter label={t('especialidade.label')}>
            <CustomSelect
              entity='especialidade'
              onChange={(especialidade?: IOption<number>) =>
                setFilter('especialidade', especialidade ?? undefined)
              }
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              value={filters.especialidade}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${
                  total === 1 ? 'atendimento não faturado' : 'atendimentos não faturados'
                }`
          }
        />
      }
    >
      <TableFilter label={t('empresa.label')}>
        <CustomSelect
          entity='empresa'
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsEmpresasForFaturamento}
          value={filters.empresa}
        />
      </TableFilter>
      <TableFilter label={t('mes.label')}>
        <CustomSelect
          entity='mes'
          onChange={(mes?: IOption<string>) => setFilter('mes', mes ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsMeses}
          value={filters.mes}
        />
      </TableFilter>
      <TableFilter label={t('ano.label')}>
        <CustomSelect
          entity='ano'
          onChange={(ano?: IOption<number>) => setFilter('ano', ano ?? undefined)}
          fetchOptions={OptionsHelper.getOptionsAnos}
          value={filters.ano}
        />
      </TableFilter>
    </TableFilters>
  )
}
